import { useState, useEffect, useRef, useCallback } from "react";
import { C, getFontFamily } from "../constants/colors";
import { PaywallPopup } from "./PaywallPopup";
import { canUseFlashCards } from "../subscription";

/* ═══════════════════════════════════════════════════════════════
   SAMAJH — Flash Cards (Premium Edition)
   Working tap buttons + swipe, rich UI, card deck metaphors
   ═══════════════════════════════════════════════════════════════ */

function extractFlashCards(ch) {
  const cards = { concept: [], formula: [] };
  ch.concepts?.forEach((c) => {
    cards.concept.push({ id: `${c.id}-main`, front: c.name, back: c.definition, backHi: c.enriched?.hookAnswer || "", anchor: c.enriched?.memoryAnchor?.fact || null, anchorEmoji: c.enriched?.memoryAnchor?.emoji || "🧠" });
    c.enriched?.concepts?.forEach((sub, i) => {
      cards.concept.push({ id: `${c.id}-s${i}`, front: `${sub.icon} ${sub.title}`, back: sub.points.map(p => p.text).join(" "), backHi: sub.points.map(p => p.explainHi).filter(Boolean).slice(0, 2).join(" "), anchor: null, anchorEmoji: null });
    });
    if (c.enriched?.formulas?.length) c.enriched.formulas.forEach((f, i) => { cards.formula.push({ id: `${c.id}-f${i}`, name: c.name, formula: f.f, meaning: f.vars }); });
    if (!c.enriched?.formulas?.length && c.formulas?.length) c.formulas.forEach((f, i) => { cards.formula.push({ id: `${c.id}-tf${i}`, name: c.name, formula: f, meaning: c.definition }); });
  });
  return cards;
}

function shuffle(a) { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; }

const SUBJ = {
  Science: { icon: "🔬", emoji: "🧪", grad: ["#065f46", "#059669", "#34d399"], soft: "#E1F5EE", text: "#065f46", dot: "#059669", pattern: "radial-gradient(circle at 80% 20%, rgba(52,211,153,0.3), transparent 50%)" },
  Maths: { icon: "📐", emoji: "🔢", grad: ["#78350f", "#b45309", "#fbbf24"], soft: "#FAEEDA", text: "#78350f", dot: "#d97706", pattern: "radial-gradient(circle at 80% 20%, rgba(251,191,36,0.3), transparent 50%)" },
  Geography: { icon: "🌍", emoji: "🗺️", grad: ["#14532d", "#16a34a", "#86efac"], soft: "#EAF3DE", text: "#14532d", dot: "#16a34a", pattern: "radial-gradient(circle at 80% 20%, rgba(134,239,172,0.3), transparent 50%)" },
  Economics: { icon: "💰", emoji: "📊", grad: ["#312e81", "#6366f1", "#a5b4fc"], soft: "#EEEDFE", text: "#312e81", dot: "#6366f1", pattern: "radial-gradient(circle at 80% 20%, rgba(165,180,252,0.3), transparent 50%)" },
};

const CT = {
  concept: { front: "linear-gradient(145deg, #065f46, #059669 50%, #10b981)", back: "linear-gradient(145deg, #ecfdf5, #d1fae5)", glow: "0 12px 40px rgba(5,150,105,0.3)", badgeF: { bg: "rgba(255,255,255,0.18)", c: "#d1fae5" }, badgeB: { bg: "#059669", c: "#fff" }, ft: "#fff", bt: "#064e3b", hi: "#047857" },
  formula: { front: "linear-gradient(145deg, #78350f, #b45309 50%, #d97706)", back: "linear-gradient(145deg, #fffbeb, #fef3c7)", glow: "0 12px 40px rgba(180,83,9,0.3)", badgeF: { bg: "rgba(255,255,255,0.18)", c: "#fef3c7" }, badgeB: { bg: "#b45309", c: "#fff" }, ft: "#fff", bt: "#78350f", hi: "#92400e" },
};

// ══════════════════════════════════════════
//  SWIPE CARD with working tap buttons
// ══════════════════════════════════════════

function SwipeCard({ card, isFormula, onSwipe, ff, onButtonSwipe, onButtonFlip }) {
  const startX = useRef(0); const curX = useRef(0); const dragging = useRef(false);
  const [off, setOff] = useState(0);
  const [flip, setFlip] = useState(false);
  const [exit, setExit] = useState(null);

  // Reset flip on new card
  useEffect(() => { setFlip(false); setOff(0); setExit(null); }, [card.id]);

  const onS = useCallback((x) => { dragging.current = true; startX.current = x; curX.current = x; }, []);
  const onM = useCallback((x) => { if (!dragging.current) return; curX.current = x; setOff(x - startX.current); }, []);
  const onE = useCallback(() => {
    if (!dragging.current) return; dragging.current = false;
    const d = curX.current - startX.current;
    if (Math.abs(d) > 80) { const dir = d > 0 ? "right" : "left"; setExit(dir); setTimeout(() => onSwipe(dir), 280); }
    else setOff(0);
  }, [onSwipe]);

  // Button-triggered swipe animation
  useEffect(() => {
    if (!onButtonSwipe) return;
    const handler = (dir) => { setExit(dir); setTimeout(() => onSwipe(dir), 280); };
    onButtonSwipe.current = handler;
  }, [onSwipe, onButtonSwipe]);

  // Button-triggered flip
  useEffect(() => {
    if (!onButtonFlip) return;
    onButtonFlip.current = () => { setFlip(f => !f); };
  }, [onButtonFlip]);

  const t = isFormula ? CT.formula : CT.concept;
  const absOff = Math.abs(off);
  const glowX = off > 30 ? `, 0 0 50px rgba(22,163,74,${Math.min(absOff/200,0.45)})` : off < -30 ? `, 0 0 50px rgba(220,38,38,${Math.min(absOff/200,0.45)})` : "";

  return (
    <div onMouseDown={e=>{e.preventDefault();onS(e.clientX)}} onMouseMove={e=>onM(e.clientX)} onMouseUp={onE} onMouseLeave={()=>{if(dragging.current)onE()}} onTouchStart={e=>onS(e.touches[0].clientX)} onTouchMove={e=>onM(e.touches[0].clientX)} onTouchEnd={onE} onClick={()=>{if(absOff<5)setFlip(!flip)}}
      style={{ position:"absolute",inset:0, transform:exit?`translateX(${exit==="right"?500:-500}px) rotate(${exit==="right"?25:-25}deg)`:`translateX(${off}px) rotate(${off*0.07}deg)`, opacity:exit?0:Math.max(0,1-absOff/500), transition:exit||!dragging.current?"all 0.3s cubic-bezier(.4,0,.2,1)":"none", zIndex:10,perspective:"1200px",cursor:"grab",userSelect:"none" }}>

      {off>50 && <div style={{ ...swipeLabel, left:22, background:"#16a34a", transform:"rotate(-12deg)", boxShadow:"0 6px 20px rgba(22,163,74,0.4)" }}>✓ GOT IT!</div>}
      {off<-50 && <div style={{ ...swipeLabel, right:22, background:"#dc2626", transform:"rotate(12deg)", boxShadow:"0 6px 20px rgba(220,38,38,0.4)" }}>✗ REVISE</div>}

      <div style={{ width:"100%",height:"100%",transformStyle:"preserve-3d",transform:flip?"rotateY(180deg)":"rotateY(0)",transition:"transform 0.5s cubic-bezier(.4,0,.2,1)" }}>

        {/* ── FRONT ── */}
        <div style={{ ...cardFace, background:t.front, boxShadow:t.glow+glowX }}>
          <div style={{ position:"absolute",top:-40,right:-40,width:150,height:150,borderRadius:"50%",background:"rgba(255,255,255,0.06)" }}/>
          <div style={{ position:"absolute",bottom:-30,left:-20,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.04)" }}/>
          <div style={{ position:"absolute",top:0,left:0,right:0,bottom:0,background:"radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.1), transparent 60%)" }}/>

          <div style={{ ...badgeStyle, background:t.badgeF.bg, color:t.badgeF.c }}>{isFormula?"⚡ FORMULA":"💡 CONCEPT"}</div>

          <div style={{ position:"relative",zIndex:1,textAlign:"center",maxWidth:"90%" }}>
            {isFormula ? (
              <div>
                <div style={{ padding:"18px 24px",borderRadius:18,background:"rgba(255,255,255,0.13)",backdropFilter:"blur(10px)",border:"1px solid rgba(255,255,255,0.18)" }}>
                  <p style={{ fontFamily:"'Courier New',monospace",fontSize:22,fontWeight:700,color:"#fff",textAlign:"center",margin:0,textShadow:"0 2px 6px rgba(0,0,0,0.15)",letterSpacing:1 }}>{card.formula}</p>
                </div>
                <p style={{ fontSize:13,color:"rgba(255,255,255,0.5)",marginTop:14,fontFamily:ff,fontWeight:500 }}>What does this mean?</p>
              </div>
            ) : (
              <h2 style={{ color:t.ft,fontFamily:ff,fontSize:22,fontWeight:700,lineHeight:1.3,margin:0,letterSpacing:"-0.02em",textShadow:"0 2px 12px rgba(0,0,0,0.2)" }}>
                {card.front}
              </h2>
            )}
          </div>

          <div style={{ position:"absolute",bottom:18,display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,0.1)",padding:"5px 12px",borderRadius:8 }}>
            <span style={{ fontSize:11 }}>👆</span>
            <span style={{ fontSize:10,color:"rgba(255,255,255,0.5)",fontFamily:ff,fontWeight:500 }}>Tap to flip</span>
          </div>
        </div>

        {/* ── BACK ── */}
        <div style={{ ...cardFace, transform:"rotateY(180deg)", background:t.back, boxShadow:t.glow+glowX, justifyContent:"flex-start", paddingTop:56 }}>
          <div style={{ ...badgeStyle, background:t.badgeB.bg, color:t.badgeB.c }}>{isFormula?"📝 MEANING":"📖 ANSWER"}</div>

          <div style={{ flex:1,overflowY:"auto",display:"flex",flexDirection:"column",justifyContent:"center",width:"100%",padding:"0 4px" }}>
            {isFormula ? (
              <div style={{ textAlign:"center" }}>
                <p style={{ fontSize:11,fontWeight:600,color:"rgba(146,64,14,0.5)",fontFamily:ff,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:1 }}>{card.name}</p>
                <div style={{ padding:"14px 20px",borderRadius:16,background:"rgba(180,83,9,0.07)",border:"1.5px solid rgba(180,83,9,0.12)",marginBottom:16 }}>
                  <p style={{ fontFamily:"'Courier New',monospace",fontSize:21,fontWeight:700,color:"#92400e",margin:0 }}>{card.formula}</p>
                </div>
                <p style={{ fontSize:13,lineHeight:1.75,color:t.bt,fontFamily:ff,margin:0 }}>{card.meaning}</p>
              </div>
            ) : (
              <div>
                <p style={{ fontSize:14,lineHeight:1.75,color:t.bt,fontFamily:ff,margin:0,textAlign:"center" }}>{card.back}</p>
                {card.anchor && (
                  <div style={{ marginTop:16,padding:"12px 14px",borderRadius:16,background:"linear-gradient(135deg,#fffbeb,#fef9c3)",border:"1.5px solid #fde68a" }}>
                    <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4 }}>
                      <span style={{ fontSize:14 }}>{card.anchorEmoji}</span>
                      <span style={{ fontSize:10,fontWeight:700,color:"#92400e",fontFamily:ff,letterSpacing:0.5 }}>MEMORY ANCHOR</span>
                    </div>
                    <p style={{ fontSize:11,lineHeight:1.55,color:"#78350f",fontFamily:ff,margin:0 }}>{card.anchor}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ textAlign:"center",paddingTop:6 }}>
            <span style={{ fontSize:10,color:"rgba(0,0,0,0.2)",fontFamily:ff }}>Swipe or tap buttons below</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const swipeLabel = { position:"absolute",top:22,zIndex:20,color:"#fff",padding:"8px 18px",borderRadius:14,fontWeight:700,fontSize:14,fontFamily:"'Outfit',sans-serif",letterSpacing:0.5 };
const cardFace = { position:"absolute",inset:0,borderRadius:24,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 24px",backfaceVisibility:"hidden",overflow:"hidden" };
const badgeStyle = { position:"absolute",top:18,left:18,padding:"5px 14px",borderRadius:10,fontSize:10,fontWeight:700,letterSpacing:1,fontFamily:"'Outfit',sans-serif",backdropFilter:"blur(8px)" };

// ══════════════════════════════════════════
//  RESULTS
// ══════════════════════════════════════════

function Results({ stats, onRestart, onRevise, onBack, ff, isHi }) {
  const total = stats.known + stats.revise;
  const pct = total > 0 ? Math.round((stats.known / total) * 100) : 0;
  const r = 56, circ = 2*Math.PI*r, dash = circ - (pct/100)*circ;
  const col = pct>=70?"#059669":pct>=40?"#d97706":"#dc2626";
  const bg = pct>=70?"linear-gradient(180deg,#ecfdf5,#d1fae5)":pct>=40?"linear-gradient(180deg,#fffbeb,#fef3c7)":"linear-gradient(180deg,#fef2f2,#fecaca)";

  return (
    <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:bg,fontFamily:ff,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24 }}>
      <div style={{ textAlign:"center",marginBottom:32 }}>
        {pct>=80 && <div style={{ fontSize:56,marginBottom:8 }}>🔥</div>}
        {pct>=40 && pct<80 && <div style={{ fontSize:56,marginBottom:8 }}>💪</div>}
        {pct<40 && <div style={{ fontSize:56,marginBottom:8 }}>📚</div>}
        <div style={{ position:"relative",display:"inline-block",marginBottom:20 }}>
          <svg width="140" height="140" viewBox="0 0 140 140"><circle cx="70" cy="70" r={r} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="9"/><circle cx="70" cy="70" r={r} fill="none" stroke={col} strokeWidth="9" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash} transform="rotate(-90 70 70)" style={{ transition:"stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)" }}/></svg>
          <div style={{ position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center" }}>
            <span style={{ fontSize:36,fontWeight:700,color:col }}>{pct}%</span>
          </div>
        </div>
        <h2 style={{ fontSize:24,fontWeight:700,color:C.text,margin:"0 0 8px" }}>{pct>=80?(isHi?"ज़बरदस्त!":"Zabardast!"):pct>=50?(isHi?"बस थोड़ा और!":"Almost there!"):(isHi?"और practice करो!":"Keep practicing!")}</h2>
        <div style={{ display:"flex",justifyContent:"center",gap:16,fontSize:14 }}>
          <span style={{ display:"flex",alignItems:"center",gap:4 }}><span style={{ width:8,height:8,borderRadius:4,background:"#16a34a",display:"inline-block" }}/><span style={{ fontWeight:600,color:"#16a34a" }}>{stats.known}</span><span style={{ color:C.textMute }}>{isHi?"सही":"correct"}</span></span>
          <span style={{ display:"flex",alignItems:"center",gap:4 }}><span style={{ width:8,height:8,borderRadius:4,background:"#dc2626",display:"inline-block" }}/><span style={{ fontWeight:600,color:"#dc2626" }}>{stats.revise}</span><span style={{ color:C.textMute }}>{isHi?"गलत":"to revise"}</span></span>
        </div>
      </div>
      <div style={{ display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:300 }}>
        {stats.revise>0 && <button onClick={onRevise} style={{ ...btnPrimary, fontFamily:ff }}>🔄 {isHi?`${stats.revise} कार्ड दोहराओ`:`Revise ${stats.revise} cards again`}</button>}
        <button onClick={onRestart} style={{ ...btnSecondary, fontFamily:ff }}>♻️ {isHi?"पूरा deck दोबारा":"Restart full deck"}</button>
        <button onClick={onBack} style={{ ...btnGhost, fontFamily:ff }}>← {isHi?"वापस":"Back to chapter"}</button>
      </div>
    </div>
  );
}

const btnPrimary = { width:"100%",padding:"15px 0",borderRadius:16,border:"none",fontWeight:700,fontSize:15,cursor:"pointer",background:C.primaryMid,color:"white",boxShadow:"0 4px 20px rgba(15,110,86,0.3)" };
const btnSecondary = { width:"100%",padding:"15px 0",borderRadius:16,border:`2px solid ${C.primaryLight}`,fontWeight:600,fontSize:14,cursor:"pointer",background:"white",color:C.primaryMid };
const btnGhost = { width:"100%",padding:12,borderRadius:14,border:"none",background:"transparent",cursor:"pointer",fontWeight:500,fontSize:13,color:C.textMute };

// ══════════════════════════════════════════
//  MINI DECK PREVIEW (visual card stack)
// ══════════════════════════════════════════

function MiniDeck({ count, colors, size = 48 }) {
  const cards = Math.min(count, 4);
  return (
    <div style={{ position:"relative",width:size,height:size*1.2 }}>
      {Array.from({length:cards}).map((_,i) => (
        <div key={i} style={{
          position:"absolute",
          width:size-4, height:size*0.7,
          borderRadius:8,
          background:`linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
          bottom: i*4, left: i*2,
          boxShadow:"0 1px 4px rgba(0,0,0,0.1)",
          border:"1px solid rgba(255,255,255,0.3)",
          transform:`rotate(${(i-1)*3}deg)`,
        }}/>
      ))}
      <div style={{ position:"absolute",bottom:-8,left:"50%",transform:"translateX(-50%)",background:"white",borderRadius:10,padding:"2px 8px",fontSize:10,fontWeight:700,color:colors[0],boxShadow:"0 1px 4px rgba(0,0,0,0.1)",whiteSpace:"nowrap" }}>
        {count}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════

export function FlashCardsPage({ profile, lang, onBack }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const currentClass = profile?.studentClass || 10;

  const [screen, setScreen] = useState("loading");
  const [flashData, setFlashData] = useState(null);
  const [error, setError] = useState(null);
  const [selSub, setSelSub] = useState(null);
  const [selCh, setSelCh] = useState(null);
  const [cType, setCType] = useState(null);
  const [paywallShow, setPaywallShow] = useState(false);
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [stats, setStats] = useState({ known:0,revise:0 });
  const [revStack, setRevStack] = useState([]);
  const buttonSwipeRef = useRef(null);
  const buttonFlipRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/data/enriched/manifest.json");
        if (!res.ok) throw new Error("manifest.json not found — run generate-manifest.js");
        const manifest = await res.json();
        const files = manifest.files.filter(f => f.class === currentClass);
        const chapters = (await Promise.all(files.map(async f => { try { const r = await fetch(`/data/enriched/${f.filename}`); return r.ok ? await r.json() : null; } catch { return null; } }))).filter(Boolean);
        const data = {};
        chapters.forEach(ch => { const s = ch.subject; if (!data[s]) data[s] = { ...(SUBJ[s]||SUBJ.Science), chapters:{} }; data[s].chapters[`Ch ${ch.ncertChapter}: ${ch.chapter}`] = extractFlashCards(ch); });
        setFlashData(data); setScreen("subjects");
      } catch (e) { setError(e.message); setScreen("error"); }
    }
    load();
  }, [currentClass]);

  const startSession = (sub,ch,type) => { const raw = flashData[sub].chapters[ch][type==="formula"?"formula":"concept"]; setCards(shuffle(raw)); setIdx(0); setStats({known:0,revise:0}); setRevStack([]); setCType(type); setScreen("session"); };

  const handleSwipe = (dir) => {
    if (dir==="right") setStats(s=>({...s,known:s.known+1}));
    else { setStats(s=>({...s,revise:s.revise+1})); setRevStack(r=>[...r,cards[idx]]); }
    if (idx+1>=cards.length) setTimeout(()=>setScreen("results"),250);
    else setIdx(i=>i+1);
  };

  // BUTTON TAP HANDLERS — these actually work!
  const handleTapRevise = () => { if (buttonSwipeRef.current) buttonSwipeRef.current("left"); else handleSwipe("left"); };
  const handleTapGotIt = () => { if (buttonSwipeRef.current) buttonSwipeRef.current("right"); else handleSwipe("right"); };
  const handleTapFlip = () => { if (buttonFlipRef.current) buttonFlipRef.current(); };

  const revise = () => { setCards(shuffle(revStack)); setIdx(0); setStats({known:0,revise:0}); setRevStack([]); setScreen("session"); };

  const Header = ({ title, backTo }) => (
    <div style={{ padding:"16px 20px",display:"flex",alignItems:"center",gap:10 }}>
      <div onClick={()=>backTo?setScreen(backTo):onBack()} style={{ cursor:"pointer",display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:12,background:"white",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",transition:"transform .1s" }} onMouseOver={e=>e.currentTarget.style.transform="scale(0.97)"} onMouseOut={e=>e.currentTarget.style.transform="scale(1)"}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12l-4-4 4-4" stroke={C.text} strokeWidth="2" strokeLinecap="round"/></svg>
        <span style={{ fontSize:13,fontWeight:600,color:C.text,fontFamily:ff }}>{isHi?"वापस":"Back"}</span>
      </div>
      {title && <span style={{ fontSize:13,color:C.textMute,fontFamily:ff,fontWeight:500 }}>/ {title}</span>}
    </div>
  );

  // ═══ LOADING ═══
  if (screen==="loading") return (
    <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"linear-gradient(180deg,#E1F5EE,#E1F5EE)",fontFamily:ff,display:"flex",alignItems:"center",justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:56,marginBottom:16,animation:"float 2s ease-in-out infinite" }}>🃏</div>
        <p style={{ fontSize:15,fontWeight:600,color:C.text }}>{isHi?"कार्ड तैयार हो रहे हैं...":"Shuffling your cards..."}</p>
        <div style={{ display:"flex",gap:6,justifyContent:"center",marginTop:12 }}>{[0,1,2].map(i=><div key={i} style={{ width:8,height:8,borderRadius:4,background:C.primaryMid,animation:`dot 1.2s ${i*0.2}s ease-in-out infinite` }}/>)}</div>
        <style>{`@keyframes float{0%,100%{transform:translateY(0) rotate(-5deg)}50%{transform:translateY(-12px) rotate(5deg)}} @keyframes dot{0%,100%{opacity:0.3;transform:scale(0.8)}50%{opacity:1;transform:scale(1.2)}}`}</style>
      </div>
    </div>
  );

  if (screen==="error") return (
    <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E1F5EE",fontFamily:ff }}><Header/><div style={{ textAlign:"center",padding:40 }}><div style={{ fontSize:48,marginBottom:16 }}>😕</div><p style={{ fontSize:16,color:C.text,fontWeight:600 }}>Couldn't load cards</p><p style={{ fontSize:13,color:C.textMute,marginTop:8,lineHeight:1.5 }}>{error}</p></div></div>
  );

  // ═══ SUBJECTS ═══
  if (screen==="subjects") {
    const subs = Object.keys(flashData);
    return (
      <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"linear-gradient(180deg,#E1F5EE 0%,#E1F5EE 40%)",fontFamily:ff }}>
        <Header/>
        <div style={{ padding:"0 20px 40px",maxWidth:460,margin:"0 auto" }}>
          {/* Title section */}
          <div style={{ textAlign:"center",marginBottom:28 }}>
            <div style={{ fontSize:44,marginBottom:8 }}>🃏</div>
            <h1 style={{ fontSize:26,fontWeight:700,color:C.text,margin:"0 0 6px",letterSpacing:"-0.03em" }}>
              {isHi?"फ़्लैश कार्ड":"Flash Cards"}
            </h1>
            <p style={{ fontSize:14,color:C.textMute,margin:0 }}>
              {isHi?`कक्षा ${currentClass} • स्वाइप करो, याद करो`:`Class ${currentClass} • Swipe right = know it, left = revise`}
            </p>
          </div>

          {/* Subject decks */}
          {subs.map((sub, si) => {
            const meta = flashData[sub]; const chs = Object.keys(meta.chapters);
            let totC=0,totF=0; Object.values(meta.chapters).forEach(c => { totC+=c.concept.length; totF+=c.formula.length; });
            const sm = SUBJ[sub]||SUBJ.Science;
            const total = totC + totF;

            return (
              <div key={sub} onClick={()=>{setSelSub(sub);setScreen("chapters")}}
                style={{ background:"white",borderRadius:22,marginBottom:16,cursor:"pointer",overflow:"hidden",boxShadow:"0 4px 20px rgba(0,0,0,0.06)",transition:"all .2s",border:"1px solid rgba(0,0,0,0.04)",animation:`slideUp 0.4s ${si*0.08}s both` }}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 12px 32px rgba(0,0,0,0.1)"}} onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,0.06)"}}>
                {/* Gradient header */}
                <div style={{ background:`linear-gradient(135deg, ${sm.grad[0]}, ${sm.grad[1]})`,padding:"20px 22px",position:"relative",overflow:"hidden" }}>
                  <div style={{ position:"absolute",inset:0,background:sm.pattern }}/>
                  <div style={{ position:"relative",zIndex:1,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                    <div>
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4 }}>
                        <span style={{ fontSize:22 }}>{sm.icon}</span>
                        <h3 style={{ fontSize:20,fontWeight:700,color:"#fff",margin:0 }}>{sub}</h3>
                      </div>
                      <p style={{ fontSize:12,color:"rgba(255,255,255,0.7)",margin:0 }}>{chs.length} {isHi?"अध्याय":"chapters"} • {total} {isHi?"कार्ड":"cards"}</p>
                    </div>
                    <MiniDeck count={total} colors={[sm.grad[0], sm.grad[2]]} />
                  </div>
                </div>
                {/* Stats row */}
                <div style={{ padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                  <div style={{ display:"flex",gap:12 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:5 }}>
                      <div style={{ width:24,height:24,borderRadius:7,background:"#E1F5EE",display:"flex",alignItems:"center",justifyContent:"center" }}><span style={{ fontSize:11 }}>💡</span></div>
                      <div><div style={{ fontSize:14,fontWeight:700,color:C.text }}>{totC}</div><div style={{ fontSize:9,color:C.textMute }}>concepts</div></div>
                    </div>
                    <div style={{ display:"flex",alignItems:"center",gap:5 }}>
                      <div style={{ width:24,height:24,borderRadius:7,background:"#FAEEDA",display:"flex",alignItems:"center",justifyContent:"center" }}><span style={{ fontSize:11 }}>⚡</span></div>
                      <div><div style={{ fontSize:14,fontWeight:700,color:C.text }}>{totF}</div><div style={{ fontSize:9,color:C.textMute }}>formulas</div></div>
                    </div>
                  </div>
                  <div style={{ padding:"8px 16px",borderRadius:10,background:sm.soft,fontSize:12,fontWeight:600,color:sm.text }}>{isHi?"शुरू करो →":"Start →"}</div>
                </div>
              </div>
            );
          })}
          <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </div>
      </div>
    );
  }

  // ═══ CHAPTERS ═══
  if (screen==="chapters") {
    const meta = flashData[selSub]; const chs = Object.keys(meta.chapters).sort((a,b) => { const na = parseInt(a.match(/Ch (\d+)/)?.[1]||0); const nb = parseInt(b.match(/Ch (\d+)/)?.[1]||0); return na - nb; }); const sm = SUBJ[selSub]||SUBJ.Science;
    return (<>
      <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E1F5EE",fontFamily:ff }}>
        <Header title={selSub} backTo="subjects"/>
        <div style={{ padding:"0 20px 32px",maxWidth:460,margin:"0 auto" }}>
          {/* Subject mini header */}
          <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:20 }}>
            <div style={{ width:44,height:44,borderRadius:14,background:`linear-gradient(135deg,${sm.grad[0]},${sm.grad[1]})`,display:"flex",alignItems:"center",justifyContent:"center" }}><span style={{ fontSize:20 }}>{sm.icon}</span></div>
            <div>
              <h2 style={{ fontSize:20,fontWeight:700,color:C.text,margin:0 }}>{selSub}</h2>
              <p style={{ fontSize:12,color:C.textMute,margin:0 }}>{chs.length} {isHi?"अध्याय":"chapters"} • {isHi?"अध्याय चुनें":"pick a chapter"}</p>
            </div>
          </div>

          {chs.map((ch,ci) => {
            const d = meta.chapters[ch]; const total = d.concept.length + d.formula.length;
            const access = canUseFlashCards(ci);
            return (
              <div key={ch} onClick={()=>{
                if (!access.allowed) { setPaywallShow(true); return; }
                setSelCh(ch);setScreen("typeSelect");
              }}
                style={{ background:"white",borderRadius:18,padding:0,marginBottom:12,cursor:"pointer",overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.06)",transition:"all .15s",animation:`slideUp 0.3s ${ci*0.05}s both`,opacity:access.allowed?1:0.6,position:"relative" }}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.08)"}} onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.06)"}}>
                {!access.allowed && (
                  <div style={{ position:"absolute",top:12,right:14,background:"rgba(0,0,0,0.06)",borderRadius:6,padding:"3px 8px",fontSize:9,fontWeight:700,color:"#6A808C" }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#6A808C" strokeWidth="2.5" strokeLinecap="round" style={{verticalAlign:"middle",marginRight:3}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                    PRO
                  </div>
                )}
                <div style={{ display:"flex",alignItems:"center",padding:"16px 18px",gap:14 }}>
                  {/* Chapter number circle */}
                  <div style={{ width:42,height:42,borderRadius:13,background:`linear-gradient(135deg,${sm.soft},white)`,border:`2px solid ${sm.dot}22`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <span style={{ fontSize:15,fontWeight:700,color:sm.dot }}>{ch.match(/Ch (\d+)/)?.[1] || ci+1}</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14,fontWeight:600,color:C.text,marginBottom:6 }}>{ch.replace(/Ch \d+: /, "")}</div>
                    {/* Card count pills */}
                    <div style={{ display:"flex",gap:6 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:3,padding:"3px 10px",borderRadius:8,background:"#E1F5EE" }}>
                        <span style={{ fontSize:10 }}>💡</span>
                        <span style={{ fontSize:11,fontWeight:600,color:"#059669" }}>{d.concept.length}</span>
                      </div>
                      {d.formula.length>0 && (
                        <div style={{ display:"flex",alignItems:"center",gap:3,padding:"3px 10px",borderRadius:8,background:"#FAEEDA" }}>
                          <span style={{ fontSize:10 }}>⚡</span>
                          <span style={{ fontSize:11,fontWeight:600,color:"#b45309" }}>{d.formula.length}</span>
                        </div>
                      )}
                      <div style={{ padding:"3px 10px",borderRadius:8,background:"#f1f5f9" }}>
                        <span style={{ fontSize:11,fontWeight:500,color:C.textMute }}>{total} total</span>
                      </div>
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke={sm.dot} strokeWidth="2" strokeLinecap="round"/></svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {paywallShow && <PaywallPopup type="flashCards" tier="free" onClose={() => setPaywallShow(false)} onUpgrade={() => { setPaywallShow(false); onBack?.(); }} lang={lang} />}
    </>);
  }

  // ═══ TYPE SELECT ═══
  if (screen==="typeSelect") {
    const d = flashData[selSub].chapters[selCh]; const sm = SUBJ[selSub]||SUBJ.Science;
    return (
      <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E1F5EE",fontFamily:ff }}>
        <Header title={selCh.replace(/Ch \d+: /,"")} backTo="chapters"/>
        <div style={{ padding:"0 20px 32px",maxWidth:460,margin:"0 auto" }}>
          <div style={{ textAlign:"center",marginBottom:28 }}>
            <div style={{ fontSize:36,marginBottom:8 }}>🎯</div>
            <h2 style={{ fontSize:20,fontWeight:700,color:C.text,margin:"0 0 6px" }}>{isHi?"कौनसा deck?":"Pick your deck"}</h2>
            <p style={{ fontSize:13,color:C.textMute,margin:0 }}>{selCh}</p>
          </div>

          {d.concept.length>0 && (
            <div onClick={()=>startSession(selSub,selCh,"concept")} style={{ borderRadius:22,marginBottom:16,cursor:"pointer",overflow:"hidden",boxShadow:"0 8px 30px rgba(5,150,105,0.2)",transition:"all .2s" }}
              onMouseOver={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow="0 16px 40px rgba(5,150,105,0.25)"}} onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 8px 30px rgba(5,150,105,0.2)"}}>
              <div style={{ background:"linear-gradient(135deg,#065f46,#059669 50%,#34d399)",padding:"28px 24px",position:"relative",overflow:"hidden" }}>
                <div style={{ position:"absolute",top:-30,right:-20,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.06)" }}/>
                <div style={{ position:"absolute",bottom:-40,left:-10,width:100,height:100,borderRadius:"50%",background:"rgba(255,255,255,0.04)" }}/>
                <div style={{ display:"flex",alignItems:"center",gap:16,position:"relative",zIndex:1 }}>
                  <div style={{ width:56,height:56,borderRadius:18,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(10px)",border:"1px solid rgba(255,255,255,0.1)" }}>
                    <span style={{ fontSize:28 }}>💡</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:20,fontWeight:700,color:"#fff",marginBottom:4 }}>Concept Cards</div>
                    <div style={{ fontSize:12,color:"#a7f3d0",lineHeight:1.4 }}>{d.concept.length} cards • {isHi?"परिभाषा, मुख्य विचार, memory anchors":"Definitions, key ideas & memory anchors"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {d.formula.length>0 && (
            <div onClick={()=>startSession(selSub,selCh,"formula")} style={{ borderRadius:22,marginBottom:16,cursor:"pointer",overflow:"hidden",boxShadow:"0 8px 30px rgba(180,83,9,0.2)",transition:"all .2s" }}
              onMouseOver={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow="0 16px 40px rgba(180,83,9,0.25)"}} onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 8px 30px rgba(180,83,9,0.2)"}}>
              <div style={{ background:"linear-gradient(135deg,#78350f,#b45309 50%,#fbbf24)",padding:"28px 24px",position:"relative",overflow:"hidden" }}>
                <div style={{ position:"absolute",top:-30,right:-20,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.06)" }}/>
                <div style={{ display:"flex",alignItems:"center",gap:16,position:"relative",zIndex:1 }}>
                  <div style={{ width:56,height:56,borderRadius:18,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(10px)",border:"1px solid rgba(255,255,255,0.1)" }}>
                    <span style={{ fontSize:28 }}>⚡</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:20,fontWeight:700,color:"#fff",marginBottom:4 }}>Formula Cards</div>
                    <div style={{ fontSize:12,color:"#fef3c7",lineHeight:1.4 }}>{d.formula.length} cards • {isHi?"सूत्र और हर variable का मतलब":"Equations & what each variable means"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {d.formula.length===0 && <div style={{ padding:20,borderRadius:16,textAlign:"center",background:"#f8fafc",border:"1.5px dashed #e2e8f0" }}><p style={{ fontSize:13,color:"#94a3b8",margin:0 }}>🚫 {isHi?"इस अध्याय में formula cards नहीं हैं":"No formula cards for this chapter"}</p></div>}
        </div>
      </div>
    );
  }

  // ═══ SESSION ═══
  if (screen==="session") {
    const total = cards.length; const progress = (idx/total)*100; const isF = cType==="formula";
    const sc = isF ? "#b45309" : "#059669";
    return (
      <div style={{ minHeight:"calc(100vh / 0.92)", zoom: 0.92,display:"flex",flexDirection:"column",background:isF?"linear-gradient(180deg,#fffbeb,#fef3c7 80%)":"linear-gradient(180deg,#ecfdf5,#d1fae5 80%)",fontFamily:ff }}>
        {/* Header */}
        <div style={{ padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div onClick={()=>setScreen("typeSelect")} style={{ padding:"8px 14px",borderRadius:12,background:"white",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",cursor:"pointer",fontSize:12,fontWeight:600,color:C.textMute }}>✕ {isHi?"बंद":"Exit"}</div>
          <div style={{ textAlign:"center" }}><div style={{ fontSize:12,fontWeight:600,color:C.text }}>{isF?"⚡":"💡"} {selCh.replace(/Ch \d+: /,"")}</div></div>
          <div style={{ display:"flex",gap:6 }}>
            <div style={{ padding:"5px 12px",borderRadius:10,background:"#dcfce7",fontSize:13,fontWeight:700,color:"#16a34a" }}>✓{stats.known}</div>
            <div style={{ padding:"5px 12px",borderRadius:10,background:"#fef2f2",fontSize:13,fontWeight:700,color:"#dc2626" }}>✗{stats.revise}</div>
          </div>
        </div>

        {/* Progress */}
        <div style={{ padding:"0 20px",marginBottom:8 }}>
          <div style={{ height:6,borderRadius:6,background:"rgba(0,0,0,0.06)",overflow:"hidden" }}>
            <div style={{ height:"100%",borderRadius:6,width:`${progress}%`,background:sc,transition:"width 0.3s ease" }}/>
          </div>
          <div style={{ textAlign:"center",marginTop:5 }}>
            <span style={{ fontSize:13,fontWeight:700,color:sc }}>{idx+1}</span>
            <span style={{ fontSize:13,color:"#94a3b8" }}> / {total}</span>
          </div>
        </div>

        {/* Card stack */}
        <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 20px 4px" }}>
          <div style={{ position:"relative",width:"100%",maxWidth:340,height:400 }}>
            {idx+2<cards.length && <div style={{ position:"absolute",inset:0,borderRadius:24,background:isF?"linear-gradient(135deg,#fef3c7,#fde68a)":"linear-gradient(135deg,#d1fae5,#a7f3d0)",transform:"scale(0.88) translateY(18px)",opacity:0.25 }}/>}
            {idx+1<cards.length && <div style={{ position:"absolute",inset:0,borderRadius:24,background:isF?"linear-gradient(135deg,#92400e,#d97706)":"linear-gradient(135deg,#047857,#10b981)",transform:"scale(0.94) translateY(9px)",opacity:0.35 }}/>}
            {idx<cards.length && <SwipeCard key={cards[idx].id} card={cards[idx]} isFormula={isF} onSwipe={handleSwipe} ff={ff} onButtonSwipe={buttonSwipeRef} onButtonFlip={buttonFlipRef}/>}
          </div>
        </div>

        {/* ACTION BUTTONS — these actually work! */}
        <div style={{ paddingBottom:28,display:"flex",justifyContent:"center",gap:32,alignItems:"center" }}>
          <button onClick={handleTapRevise} style={{ width:64,height:64,borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",border:"2.5px solid #fecaca",background:"white",cursor:"pointer",boxShadow:"0 4px 16px rgba(220,38,38,0.12)",transition:"all .15s",flexDirection:"column",gap:2 }}
            onMouseOver={e=>{e.currentTarget.style.transform="scale(1.08)";e.currentTarget.style.boxShadow="0 6px 24px rgba(220,38,38,0.2)"}} onMouseOut={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.boxShadow="0 4px 16px rgba(220,38,38,0.12)"}}>
            <span style={{ fontSize:22,color:"#dc2626" }}>✗</span>
            <span style={{ fontSize:8,fontWeight:600,color:"#dc2626",fontFamily:ff }}>{isHi?"नहीं":"NOPE"}</span>
          </button>

          <button onClick={handleTapFlip} style={{ width:44,height:44,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",border:"2px solid #e2e8f0",background:"white",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.04)",transition:"all .15s" }}
            onMouseOver={e=>{e.currentTarget.style.transform="scale(1.1)"}} onMouseOut={e=>{e.currentTarget.style.transform="scale(1)"}}>
            <span style={{ fontSize:16 }}>🔄</span>
          </button>

          <button onClick={handleTapGotIt} style={{ width:64,height:64,borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",border:"2.5px solid #bbf7d0",background:"white",cursor:"pointer",boxShadow:"0 4px 16px rgba(22,163,74,0.12)",transition:"all .15s",flexDirection:"column",gap:2 }}
            onMouseOver={e=>{e.currentTarget.style.transform="scale(1.08)";e.currentTarget.style.boxShadow="0 6px 24px rgba(22,163,74,0.2)"}} onMouseOut={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.boxShadow="0 4px 16px rgba(22,163,74,0.12)"}}>
            <span style={{ fontSize:22,color:"#16a34a" }}>✓</span>
            <span style={{ fontSize:8,fontWeight:600,color:"#16a34a",fontFamily:ff }}>{isHi?"आता है":"GOT IT"}</span>
          </button>
        </div>
      </div>
    );
  }

  if (screen==="results") return (<>
    <Results stats={stats} ff={ff} isHi={isHi} onRestart={()=>startSession(selSub,selCh,cType)} onRevise={revise} onBack={()=>setScreen("typeSelect")}/>
    {paywallShow && <PaywallPopup type="flashCards" tier="free" onClose={() => setPaywallShow(false)} onUpgrade={() => { setPaywallShow(false); onBack?.(); }} lang={lang} />}
  </>);
  return paywallShow ? <PaywallPopup type="flashCards" tier="free" onClose={() => setPaywallShow(false)} onUpgrade={() => { setPaywallShow(false); onBack?.(); }} lang={lang} /> : null;
}
